import { useState } from 'react';
import { Download, File, FileText, FileSpreadsheet, FileArchive, Film, Music, X, ExternalLink } from 'lucide-react';
import { formatFileSize } from './useChatAttachments';

interface AttachmentData {
  file_name: string;
  file_type: string;
  file_size: number;
  public_url: string;
  width_px?: number | null;
  height_px?: number | null;
}

interface MessageAttachmentsProps {
  attachments: AttachmentData[];
}

function getFileIcon(type: string) {
  if (type.startsWith('video/')) return Film;
  if (type.startsWith('audio/')) return Music;
  if (type.includes('pdf')) return FileText;
  if (type.includes('spreadsheet') || type.includes('csv') || type.includes('excel')) return FileSpreadsheet;
  if (type.includes('zip') || type.includes('rar') || type.includes('tar') || type.includes('gz')) return FileArchive;
  if (type.includes('document') || type.includes('text')) return FileText;
  return File;
}

function isImage(type: string): boolean {
  return type.startsWith('image/');
}

function isVideo(type: string): boolean {
  return type.startsWith('video/');
}

function isAudio(type: string): boolean {
  return type.startsWith('audio/');
}

// Constrain image dimensions for display
function getDisplayDimensions(
  widthPx: number | null | undefined,
  heightPx: number | null | undefined,
  maxWidth = 320,
  maxHeight = 320
): { width: number; height: number } | null {
  if (!widthPx || !heightPx) return null;

  let w = widthPx;
  let h = heightPx;

  if (w > maxWidth) {
    h = Math.round(h * (maxWidth / w));
    w = maxWidth;
  }
  if (h > maxHeight) {
    w = Math.round(w * (maxHeight / h));
    h = maxHeight;
  }

  return { width: Math.max(w, 40), height: Math.max(h, 40) };
}

const attachmentSurface: React.CSSProperties = {
  background: 'var(--surface-raised)',
  border: '1px solid var(--border-hairline)',
  borderRadius: 'var(--radius-sm)',
};

function ImageAttachment({ attachment }: { attachment: AttachmentData }) {
  const [loaded, setLoaded] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [error, setError] = useState(false);

  const dims = getDisplayDimensions(attachment.width_px, attachment.height_px);

  if (error) {
    // Fallback to file card on load error
    return <FileAttachment attachment={attachment} />;
  }

  return (
    <>
      <div
        className="relative overflow-hidden cursor-pointer group"
        style={{
          ...attachmentSurface,
          ...(dims ? { width: dims.width, height: dims.height } : { maxWidth: 320 }),
        }}
        onClick={() => setLightbox(true)}
      >
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--border-strong)', borderTopColor: 'transparent' }}
            />
          </div>
        )}
        <img
          src={attachment.public_url}
          alt={attachment.file_name}
          className={`w-full h-full object-cover transition-opacity ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
        {/* Hover overlay with open affordance */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <ExternalLink
            className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg"
          />
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center cursor-zoom-out"
          style={{ background: 'var(--surface-overlay)' }}
          onClick={() => setLightbox(false)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightbox(false);
            }}
            className="absolute right-4 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-10"
            style={{ top: 'calc(16px + var(--sat))' }}
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <a
            href={attachment.public_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="absolute right-20 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-10"
            style={{ top: 'calc(16px + var(--sat))' }}
            title="Open original"
          >
            <Download className="w-5 h-5" />
          </a>
          <img
            src={attachment.public_url}
            alt={attachment.file_name}
            className="max-w-[90vw] max-h-[80dvh] object-contain"
            style={{ borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-pop)' }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

function VideoAttachment({ attachment }: { attachment: AttachmentData }) {
  const dims = getDisplayDimensions(attachment.width_px, attachment.height_px, 400, 300);

  return (
    <div
      className="overflow-hidden"
      style={{ ...attachmentSurface, ...(dims ? { maxWidth: dims.width } : { maxWidth: 400 }) }}
    >
      <video
        src={attachment.public_url}
        controls
        preload="metadata"
        className="w-full"
        style={{ borderRadius: 'var(--radius-sm)' }}
      >
        <source src={attachment.public_url} type={attachment.file_type} />
      </video>
    </div>
  );
}

function AudioAttachment({ attachment }: { attachment: AttachmentData }) {
  return (
    <div
      className="overflow-hidden p-2"
      style={{ ...attachmentSurface, maxWidth: 360 }}
    >
      <div
        className="truncate mb-1.5"
        style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
          color: 'var(--text-body)',
        }}
      >
        {attachment.file_name}
      </div>
      <audio src={attachment.public_url} controls preload="metadata" className="w-full h-8" />
    </div>
  );
}

function FileAttachment({ attachment }: { attachment: AttachmentData }) {
  const FileIcon = getFileIcon(attachment.file_type);

  return (
    <a
      href={attachment.public_url}
      target="_blank"
      rel="noopener noreferrer"
      className="hover-lift pressable flex items-center gap-2.5 px-3 py-2 no-underline max-w-[280px]"
      style={attachmentSurface}
    >
      <div
        className="flex-shrink-0 p-2"
        style={{ background: 'var(--surface-active)', borderRadius: 'var(--radius-sm)' }}
      >
        <FileIcon className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="truncate"
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
            color: 'var(--text-body)',
          }}
          title={attachment.file_name}
        >
          {attachment.file_name}
        </div>
        <div className="t-num" style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
          {formatFileSize(attachment.file_size)}
        </div>
      </div>
      <Download className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-faint)' }} />
    </a>
  );
}

export default function MessageAttachments({ attachments }: MessageAttachmentsProps) {
  if (!attachments || attachments.length === 0) return null;

  // Separate images from other files for layout
  const images = attachments.filter(a => isImage(a.file_type));
  const videos = attachments.filter(a => isVideo(a.file_type));
  const audio = attachments.filter(a => isAudio(a.file_type));
  const files = attachments.filter(
    a => !isImage(a.file_type) && !isVideo(a.file_type) && !isAudio(a.file_type)
  );

  return (
    <div className="mt-1.5 space-y-1.5">
      {/* Image grid */}
      {images.length > 0 && (
        <div className={`flex flex-wrap gap-1.5 ${images.length === 1 ? '' : 'max-w-[660px]'}`}>
          {images.map((img, i) => (
            <ImageAttachment key={`img-${i}`} attachment={img} />
          ))}
        </div>
      )}

      {/* Videos */}
      {videos.map((vid, i) => (
        <VideoAttachment key={`vid-${i}`} attachment={vid} />
      ))}

      {/* Audio */}
      {audio.map((aud, i) => (
        <AudioAttachment key={`aud-${i}`} attachment={aud} />
      ))}

      {/* File cards */}
      {files.length > 0 && (
        <div className="flex flex-col gap-1">
          {files.map((file, i) => (
            <FileAttachment key={`file-${i}`} attachment={file} />
          ))}
        </div>
      )}
    </div>
  );
}
